import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import QRCode from "qrcode.react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { getSwishPaymentStatus } from "../queries/swish";
import { cancelQrOpenWatchdog } from "../services/utils/qrWatchdog";

/* ───────────────── animations ───────────────── */
const gradientShift = keyframes`
    0% { background-position: 0 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
`;
const fadeInScale = keyframes`
    from { opacity: 0; transform: scale(0.8) translateY(30px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
`;
const pulse = keyframes`
    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(63, 196, 138, 0.4); }
    50% { transform: scale(1.02); box-shadow: 0 0 0 10px rgba(63, 196, 138, 0); }
`;
const qrGlow = keyframes`
    0%, 100% { box-shadow: 0 0 20px rgba(63, 196, 138, 0.3); }
    50% { box-shadow: 0 0 40px rgba(63, 196, 138, 0.6); }
`;
const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
`;
const sparkle = keyframes`
    0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
    50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

/* ===== Page ===== */
export default function QRCodeScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    const { setCartItems } = useCart();

    const navState = location.state || {};
    const initialQr = navState.qrCode || null;

    const [qrCode, setQrCode] = useState(initialQr);
    const [isVisible, setIsVisible] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    /* Entrance effects */
    useEffect(() => {
        setIsVisible(true);
        setShowSuccess(true);
    }, []);

    /* Cancel any armed watchdog immediately when QR page mounts */
    useEffect(() => {
        try { cancelQrOpenWatchdog("qr_page_mounted"); } catch {}
    }, []);

    /* Fallback: if no qrCode passed (race) attempt one status refetch, then optional legacy unlock fallback */
    useEffect(() => {
        (async () => {
            if (qrCode) return; // already have
            const pid = new URLSearchParams(location.search).get("paymentId");
            if (!pid) return;
            try {
                await new Promise(r => setTimeout(r, 500)); // brief delay for backend propagation
                const resp = await getSwishPaymentStatus(pid);
                const code = resp?.qrCode ?? resp?.data?.qrCode;
                if (code) {
                    setQrCode(code);
                }
            } catch {}
        })();
    }, [qrCode, location.search]);

    return (
        <>
            <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
            <style>{`*{box-sizing:border-box;} html,body,#root{overflow-x:hidden;max-width:100%;}`}</style>

            <Container $isVisible={isVisible}>
                {/* Floating Success Elements */}
                <FloatingElement $delay="0s" $top="10%" $left="10%">✨</FloatingElement>
                <FloatingElement $delay="1s" $top="20%" $right="15%">🎉</FloatingElement>
                <FloatingElement $delay="2s" $top="70%" $left="5%">⭐</FloatingElement>
                <FloatingElement $delay="0.5s" $bottom="10%" $right="10%">💫</FloatingElement>

                <QrCard $showSuccess={showSuccess}>
                    <SuccessHeader>
                        <Title>Tack för ditt köp!</Title>
                        <SuccessSubtitle>Din beställning är genomförd</SuccessSubtitle>
                    </SuccessHeader>

                    {qrCode && (
                        <QRSection>
                            <QRWrapper>
                                <QRCode value={String(qrCode)} size={220} bgColor="#ffffff" fgColor="#000000" includeMargin />
                                <QRGlow />
                            </QRWrapper>

                            <InstructionCard>
                                <InstructionIcon>📱</InstructionIcon>
                                <InstructionText>Rikta QR-koden mot kameralinsen på enheten för att öppna</InstructionText>
                            </InstructionCard>
                        </QRSection>
                    )}

                    <ButtonSection>
                        <BackButton onClick={() => {
                            ["unlockCode","unlockCodeExpiry","cartItems","historySaved","paymentConfirmed","paymentStatus"].forEach(k=>localStorage.removeItem(k));
                            setCartItems([]);
                            navigate('/products');
                        }}>
                            <ButtonIcon>🏠</ButtonIcon>
                            Tillbaka till produkter
                        </BackButton>
                    </ButtonSection>
                </QrCard>
            </Container>
        </>
    );
}

/* ───────────── styled (pure JS) ───────────── */
const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%);
    background-size: 400% 400%;
    animation: ${gradientShift} 12s ease-in-out infinite;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    position: relative;
    overflow: hidden;
    opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
    transform: ${({ $isVisible }) => ($isVisible ? "translateY(0)" : "translateY(20px)")};
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;

const FloatingElement = styled.div`
    position: absolute;
    font-size: 24px;
    animation: ${float} 3s ease-in-out infinite;
    animation-delay: ${({ $delay }) => $delay || "0s"};
    opacity: 0.7;
    z-index: 1;
    ${({ $top }) => $top && css`top: ${$top};`}
    ${({ $bottom }) => $bottom && css`bottom: ${$bottom};`}
    ${({ $left }) => $left && css`left: ${$left};`}
    ${({ $right }) => $right && css`right: ${$right};`}
`;

const QrCard = styled.div`
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 28px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    padding: 40px 32px;
    max-width: 460px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    animation: ${fadeInScale} 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
    z-index: 10;
    ${({ $showSuccess }) => $showSuccess && css`
        animation: ${fadeInScale} 0.8s cubic-bezier(0.4,0,0.2,1), ${pulse} 2s ease-in-out;
    `}
`;

const SuccessHeader = styled.div` text-align: center; margin-bottom: 32px; `;
const SuccessSubtitle = styled.p` font-size: 16px; color: #64748b; margin: 0; font-weight: 500; `;
const Title = styled.h1` font-size: 28px; font-weight: 700; color: #1e293b; text-align: center; margin: 0 0 8px 0; line-height: 1.2; `;


const QRSection = styled.div` display: flex; flex-direction: column; align-items: center; margin-bottom: 32px; `;
const QRWrapper = styled.div`
    position: relative;
    background: #ffffff;
    padding: 24px;
    border-radius: 20px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
    animation: ${qrGlow} 3s ease-in-out infinite;
    z-index: 0;
`;
const QRGlow = styled.div`
    position: absolute;
    top: -4px; left: -4px; right: -4px; bottom: -4px;
    background: linear-gradient(45deg, #10b981, #3b82f6, #8b5cf6, #10b981);
    border-radius: 24px;
    z-index: -1;
    opacity: 0.3;
    animation: ${sparkle} 4s ease-in-out infinite;
`;

const InstructionCard = styled.div`
    display: flex; align-items: center; gap: 12px;
    background: rgba(59,130,246,0.1);
    padding: 16px 20px; border-radius: 12px;
    border: 1px solid rgba(59,130,246,0.2);
    max-width: 320px;
`;
const InstructionIcon = styled.div` font-size: 24px; flex-shrink: 0; `;
const InstructionText = styled.div` font-size: 14px; color: #1e40af; font-weight: 500; line-height: 1.4; `;

const ButtonSection = styled.div` width: 100%; display: flex; flex-direction: column; gap: 16px; `;
const BackButton = styled.button`
    width: 100%;
    background: linear-gradient(135deg, #000000 0%, #1f2937 100%);
    color: #ffffff; border: none; border-radius: 16px;
    padding: 18px 24px; font-size: 16px; font-weight: 600; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
    &:hover { background: linear-gradient(135deg, #1f2937 0%, #374151 100%); transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0,0,0,0.3); }
    &:active { transform: translateY(0); }
`;
const ButtonIcon = styled.span` font-size: 18px; `;

